<template>
  <div class="bg-white rounded-lg border border-border overflow-hidden">
    <!-- Card Header -->
    <div class="flex items-center gap-2 px-6 py-4 border-b border-border">
      <Receipt class="w-4 h-4 text-muted-foreground" />
      <h3 class="text-sm font-semibold text-gray-800">Billing History</h3>
    </div>

    <!-- Filters -->
    <PaymentFilters
      :status-counts="statusCounts"
      :total-count="allCount"
      :selected-status="selectedStatus"
      @filters-change="$emit('filters-change', $event)"
    />

    <!-- Payments State (Loading/Error/Empty) -->
    <PaymentsState
      :is-loading="isLoading"
      :error="error"
      :has-payments="hasPayments"
      @retry="$emit('retry')"
    />

    <!-- Payments Table -->
    <PaymentsTable
      v-if="hasPayments && !isLoading && !error"
      :payments="payments"
    />

    <!-- Footer: Results + Pagination -->
    <div v-if="hasPayments && !isLoading && !error" class="flex items-center justify-between border-t border-border bg-white px-6 py-3">
      <ResultsInfo
        :start-item="paginationLogic.getStartItem()"
        :end-item="paginationLogic.getEndItem()"
        :total-count="totalCount"
      />
      <PaginationControls
        v-if="pagination && paginationLogic.shouldShowPagination.value"
        :page-numbers="paginationLogic.getPageNumbers()"
        :current-page="pagination.current_page"
        :has-previous="pagination.has_previous"
        :has-next="pagination.has_next"
        :disabled="isLoading"
        @page-select="paginationLogic.goToPage"
        @previous="paginationLogic.goToPreviousPage"
        @next="paginationLogic.goToNextPage"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Receipt } from 'lucide-vue-next'
import PaymentFilters from '@/components/subscription/PaymentFilters.vue'
import PaymentsTable from '@/components/subscription/PaymentsTable.vue'
import PaymentsState from '@/components/subscription/PaymentsState.vue'
import PaginationControls from '@/components/subscription/PaginationControls.vue'
import ResultsInfo from '@/components/subscription/ResultsInfo.vue'
import { usePagination } from '@/composables/usePagination'
import type { SubscriptionPayment, PaginationInfo, PaymentStatus } from '@/types/subscription'

interface Props {
  payments: SubscriptionPayment[]
  isLoading: boolean
  error: string | null
  hasPayments: boolean
  pagination: PaginationInfo | null
  totalCount: number
  allCount: number // Total count of all payments (unfiltered, for "All Payments" display)
  statusCounts: Record<PaymentStatus, number>
  selectedStatus: PaymentStatus | 'all'
}

interface Emits {
  (e: 'filters-change', filters: any): void
  (e: 'page-change', page: number): void
  (e: 'retry'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

// Pagination logic using composable
const paginationLogic = usePagination({
  pagination: computed(() => props.pagination),
  totalCount: computed(() => props.totalCount),
  isLoading: computed(() => props.isLoading),
  onPageChange: (page: number) => emit('page-change', page)
})
</script>
